import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { typeColor, formatRange } from '../lib/format.js';

const SICHTEN = [
  { key: 'pvp', label: 'Trainerkampf' },
  { key: 'raid', label: 'Raids' },
];

const LIGA = { great: 'Superliga', ultra: 'Hyperliga', master: 'Meisterliga' };

const ROLLE = {
  featured: 'Im Mittelpunkt',
  increased_spawn: 'Häufiger in der Wildnis',
  research_reward: 'Forschungsbelohnung',
  raid_boss: 'Raid',
  egg: 'Aus dem Ei',
};

/**
 * Was lohnt sich gerade zu fangen?
 *
 * Führt zusammen, was sonst über Events und Raids verstreut liegt. Die
 * Trennung nach Herkunft ist der Kern: bei einem Wildspawn kannst du fangen,
 * bis das Muster stimmt — aus Raid oder Ei bekommst du ein Exemplar und musst
 * nehmen, was kommt.
 */
export default function FangenPage() {
  const [daten, setDaten] = useState(null);
  const [sicht, setSicht] = useState('pvp');
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    api.fangen().then(setDaten).catch((e) => setFehler(e.message));
  }, []);

  if (fehler) return <div className="error">{fehler}</div>;
  if (!daten) return <div className="skeleton" style={{ height: 240 }} />;

  const aktuell = daten[sicht];

  return (
    <>
      <section className="hero">
        <span className="hero__tag">Lohnt sich</span>
        <h1>Was jetzt fangen?</h1>
        <p className="hero__sub">
          Arten aus laufenden und kommenden Events, sortiert danach, was sie
          taugen — nicht danach, wann sie kommen.
        </p>
      </section>

      <div className="filters">
        {SICHTEN.map((s) => (
          <button
            key={s.key}
            type="button"
            className={sicht === s.key ? 'chip chip--on' : 'chip'}
            onClick={() => setSicht(s.key)}
          >
            {s.label} ({daten[s.key].wildCount + daten[s.key].fixedCount})
          </button>
        ))}
      </div>

      <Abschnitt
        titel="Aus der Wildnis"
        text="Hier lohnt gezieltes Farmen: du kannst fangen, bis das Muster stimmt."
        eintraege={aktuell.wild}
        sicht={sicht}
      />

      <Abschnitt
        titel="Aus Raid, Ei oder Forschung"
        text="Ein Versuch, ein Exemplar. Auf ein Muster hin fangen geht hier nicht."
        eintraege={aktuell.fixed}
        sicht={sicht}
      />
    </>
  );
}

function Abschnitt({ titel, text, eintraege, sicht }) {
  if (eintraege.length === 0) {
    return (
      <section className="gruppe">
        <h2>{titel}</h2>
        <div className="empty">Zurzeit nichts Lohnendes.</div>
      </section>
    );
  }

  return (
    <section className="gruppe">
      <h2>{titel}</h2>
      <p className="gruppe__text">{text}</p>

      <div className="stack">
        {eintraege.map((e) => (
          <FangKarte key={e.slug + e.eventTitle + (e.pvp?.league ?? '')} eintrag={e} sicht={sicht} />
        ))}
      </div>
    </section>
  );
}

function FangKarte({ eintrag: e, sicht }) {
  const wertung = sicht === 'pvp'
    ? { stufe: e.pvp.tier, kopf: LIGA[e.pvp.league] ?? e.pvp.league, platz: e.pvp.rankNo }
    : { stufe: e.raid.tier, kopf: e.raid.role === 'raid_boss' ? 'Als Raid-Boss' : 'Als Angreifer', platz: null };

  return (
    <article className="fang-karte">
      <span className={'tier tier--' + wertung.stufe}>{wertung.stufe}</span>

      <div className="fang-karte__mitte">
        <div className="spread">
          <div>
            <Link to={'/pokemon/' + e.slug} className="fang-karte__name">
              {e.name}
              {e.form && <span className="mon-card__form"> {e.form}</span>}
            </Link>
            <div className="card__meta">
              {wertung.kopf}
              {wertung.platz && ' · Platz ' + wertung.platz.toLocaleString('de-DE')}
              {e.raid?.attackType && ' · ' + e.raid.attackType}
            </div>
          </div>
        </div>

        <div className="inline">
          {e.types.map((t) => (
            <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
          ))}
          <span className="badge badge--soft">{ROLLE[e.role] ?? e.role}</span>
          {e.shinyBoosted && <span className="badge badge--soft">Shiny-Chance erhöht</span>}
          {e.isLegendary && <span className="badge badge--soft">Legendär</span>}
        </div>

        {e.covered && (
          <p className="rang-zeile__belegt">
            Deckst du schon ab: {e.covered.name} ({e.covered.wert}) besetzt {e.covered.typ}
            stärker, als diese Art überhaupt werden kann.
          </p>
        )}

        <div className="card__meta">
          {e.eventSlug
            ? <Link to={'/event/' + e.eventSlug}>{e.eventTitle}</Link>
            : e.eventTitle}
          {' · '}
          {e.openEnded ? 'läuft bis auf Weiteres' : formatRange(e.startsAt, e.endsAt)}
        </div>
      </div>
    </article>
  );
}
